import React, { useState, useEffect, useRef } from 'react'
import axiosInstance from '../../../services/axiosInstance'
import { useNavigate } from 'react-router-dom'
import { 
  CheckCircle2, Circle, FileText, ChevronRight,
  AlertCircle, Sparkles, AlertTriangle, User
} from 'lucide-react'
import './styles.scss'

export default function ChecklistPanel({ appContext, onRefreshDashboard }) {
  const navigate = useNavigate()
  const [checklistItems, setChecklistItems] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [isProfileComplete, setIsProfileComplete] = useState(false)

  useEffect(() => {
    if (appContext?.application?.id) {
      fetchData()
    }
  }, [appContext?.application?.id])

  const fetchData = async () => {
    try {
      setLoading(true)
      const appId = appContext.application.id
      
      // Fetch Checklist
      const chkRes = await axiosInstance.get(`/api/checklists/application/${appId}`)
      let items = chkRes.data
      if (items.length === 0) {
        const genRes = await axiosInstance.post(`/api/checklists/generate/${appId}`)
        items = genRes.data
      }
      setChecklistItems(items)

      // Fetch Documents
      const docRes = await axiosInstance.get(`/api/documents/application/${appId}`)
      setDocuments(docRes.data || [])

      // Fetch Profile
      const profRes = await axiosInstance.get('/api/profiles/me')
      const profile = profRes.data?.profile
      setIsProfileComplete(Boolean(profile?.legalFirstName && profile?.legalLastName && profile?.dateOfBirth && profile?.countryOfCitizenship && profile?.phone))
      
    } catch (e) {
      console.error('Failed to load checklist data', e)
    } finally {
      setLoading(false)
    }
  }

  if (loading || !appContext) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(37,99,235,0.2)', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    )
  }

  const getDocumentForType = (type) => documents.find(d => d.documentType === type)

  const requiredItems = checklistItems.filter(i => i.required !== false)
  const completedRequiredCount = requiredItems.filter(i => {
    const doc = getDocumentForType(i.documentType)
    const isRejected = doc?.humanReviewStatus === 'REJECTED' || doc?.aiInsight?.decisionStatus === 'REJECTED'
    if (isRejected) return false;
    
    return i.status === 'Completed' || (doc && doc.humanReviewStatus === 'VERIFIED');
  }).length + (isProfileComplete ? 1 : 0)
  const totalRequiredCount = requiredItems.length + 1

  return (
    <div className="checklist-panel">
      <div className="checklist-panel-header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>My Document Checklist</h2>
          <p style={{ color: '#64748b', margin: 0, marginTop: '4px' }}>Required documents for {appContext.universityName}</p>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '12px' }}>
          {completedRequiredCount}/{totalRequiredCount} completed
        </div>
      </div>

      <div className="checklist-container">
        {/* Static Complete Profile Item */}
        <div 
          className={`checklist-card ${isProfileComplete ? 'completed' : ''}`} 
          onClick={() => navigate('/complete-profile', { state: { fromChecklist: true } })}
          style={{ cursor: 'pointer' }}
        >
          <div className="checklist-card__header">
            <div className="header-left">
              <div className="status-icon">
                {isProfileComplete ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
              </div>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem', color: '#1d4ed8', flexShrink: 0 }}>
                <User size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b' }}>1. Complete Profile</h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px', lineHeight: '1.4' }}>
                  Provide your personal and academic details
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '0.85rem', color: isProfileComplete ? '#16a34a' : '#64748b', fontWeight: '500' }}>
                {isProfileComplete ? 'Completed' : 'Pending'}
              </span>
              <ChevronRight size={20} color="#94a3b8" />
            </div>
          </div>
        </div>

        {/* Dynamic Document Items */}
        {checklistItems.map((item, index) => {
          const doc = getDocumentForType(item.documentType)
          const isUploaded = !!doc
          const isCompleted = item.status === 'Completed' || (doc && doc.humanReviewStatus === 'VERIFIED')
          const isRejected = doc?.humanReviewStatus === 'REJECTED' || doc?.aiInsight?.decisionStatus === 'REJECTED'

          return (
            <div 
              className={`checklist-card ${isCompleted ? 'completed' : isRejected ? 'rejected' : ''}`} 
              key={item.id}
              onClick={() => {
                navigate(`/document-upload/${appContext.application.id}/${item.documentType}`)
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="checklist-card__header">
                <div className="header-left">
                  <div className="status-icon">
                    {isCompleted ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
                  </div>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem', color: '#1d4ed8', flexShrink: 0 }}>
                    <FileText size={20} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b' }}>
                      {index + 2}. {item.title} {!item.required && <span className="tag-optional">Optional</span>}
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px', lineHeight: '1.4' }}>
                      {item.instructions || item.description}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '0.75rem', color: isRejected ? '#dc2626' : (isCompleted ? '#16a34a' : '#64748b'), fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {isUploaded ? (isRejected ? 'Needs Replacement' : isCompleted ? 'Verified' : 'Under Review') : 'Pending'}
                  </span>
                  <ChevronRight size={20} color="#94a3b8" />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
